import React from "react";
import { AlertTriangle, RefreshCw } from "lucide-react";

/**
 * Generic React error boundary.
 *
 * Catches render-time exceptions from any descendant so a single bad
 * state update (e.g. a malformed SSE payload) can't blank the whole
 * inbox. The "Try again" button resets internal state — the page's
 * own effects will then re-mount and re-fetch. App.js wraps <Routes>
 * in one of these for app-wide coverage, and per-page boundaries nest
 * inside it for a nicer UX (local recovery, not a whole blank page).
 *
 * ═══════════════════════════════════════════════════════════════════
 *  THE ROUTE-LEVEL WRAP IS NOT OPTIONAL
 * ═══════════════════════════════════════════════════════════════════
 * This comment used to claim the route-level wrap existed. It did
 * not — the only use in the whole app was around <InboxPage />. So an
 * uncaught render throw anywhere else unmounted the entire tree, and
 * because React 18's createRoot empties #root when it does that, the
 * customer saw a BLACK SCREEN: no message, no retry, nothing to
 * screenshot. That is how a 422 on the checkout form turned into a
 * support ticket that read "the screen is black and nothing happens"
 * and took a day to explain (2026-10-06).
 *
 * `resetKey` is how the app-wide one recovers: App.js passes the
 * current pathname, so navigating somewhere else clears a stuck
 * fallback. Without it the first crash would freeze the shell until a
 * hard reload, which is a worse failure than the one being fixed.
 */
class ErrorBoundary extends React.Component {
  constructor(props) {
    super(props);
    this.state = { hasError: false, error: null };
  }

  static getDerivedStateFromError(error) {
    return { hasError: true, error };
  }

  componentDidCatch(error, info) {
    // Send to console for local debugging; a future integration could
    // ship this to Sentry/LogRocket. "React crash" is the canonical
    // prefix so CI logs stay searchable; the label says WHICH boundary
    // caught it, which the old fixed "Inbox crash:" could not — it was
    // printed by the inbox boundary and would have been printed by
    // every other one too.
    // eslint-disable-next-line no-console
    console.error(`React crash (${this.props.label || "unlabelled"}):`,
      error, info);
  }

  // Recover when the caller says the context changed — App.js passes the
  // pathname, so a crash on one page does not follow the customer to the
  // next one. Guarded on hasError so a normal re-render never touches
  // state and this cannot loop.
  componentDidUpdate(prevProps) {
    if (this.state.hasError && prevProps.resetKey !== this.props.resetKey) {
      this.setState({ hasError: false, error: null });
    }
  }

  handleRetry = () => {
    this.setState({ hasError: false, error: null });
  };

  render() {
    if (this.state.hasError) {
      const name = this.props.label || "this panel";
      return (
        <div
          data-testid="error-boundary-fallback"
          className="flex flex-col items-center justify-center gap-3 p-8 text-center"
        >
          <AlertTriangle className="w-10 h-10 text-amber-500" />
          <p className="text-sm text-muted-foreground max-w-sm">
            Something went wrong loading {name}. Your data is safe — this was a
            display-side error.
          </p>
          <button
            type="button"
            onClick={this.handleRetry}
            className="inline-flex items-center gap-2 px-4 py-2 rounded-full border border-border text-sm hover:bg-muted transition-colors"
            data-testid="error-boundary-retry"
          >
            <RefreshCw className="w-4 h-4" />
            Try again
          </button>
          {process.env.NODE_ENV !== "production" && this.state.error && (
            <pre className="text-xs text-red-600 dark:text-red-400 mt-2 max-w-full overflow-auto">
              {String(this.state.error?.message || this.state.error)}
            </pre>
          )}
        </div>
      );
    }
    return this.props.children;
  }
}

export default ErrorBoundary;
